<template>
  <b-card>
    <div slot="header" v-if="!spinners.contentIsLoading">
      <b-row>
        <b-col cols="6" class="mt-1">
          <span>{{ 'products_list_title' | translate('global') }}</span>
        </b-col>
        <b-col cols="6">
          <div class="pull-right">
            <Create :transportsClassifier="transportsClassifier"/>
          </div>
        </b-col>
      </b-row>
    </div>

    <div v-if="spinners.contentIsLoading" class="text-center">
      <b-spinner type="grow" variant="primary"/>
    </div>

    <div v-else>
      <!--------FILTERS---------------------->
      <b-row>
        <b-col cols="6" xl="4">
          <b-form-group>
            <b-input :placeholder="'search_by_name_filter' | translate('global')"
                     v-model="filters.name">
            </b-input>
          </b-form-group>
        </b-col>
        <b-col cols="6" xl="4">
          <b-form-group>
            <treeselect :options="statusOptions" :default-expand-level="1"
                        :placeholder="'select_status' | translate('global')"
                        v-model="filters.is_active"/>
          </b-form-group>
        </b-col>
        <b-col cols="6" xl="4">
          <b-form-checkbox id="has_errors" class="mt-2 mb-md-3"
                           v-model="filters.has_category" :value="0">
            {{ 'only_products_without_categories' | translate('global') }}
          </b-form-checkbox>
        </b-col>

        <b-col cols="12" xl="9" class="mb-2">
          <b-button variant="primary" block @click="getData()"
                    :disabled="disabledButtons.filter">{{ 'filter' | translate('global') }}</b-button>
        </b-col>
        <b-col cols="12" xl="3" class="mb-2">
          <b-button variant="secondary" block @click="cleanFilter()"
                    :disabled="disabledButtons.filter">{{ 'filter_clean' | translate('global') }}</b-button>
        </b-col>
      </b-row>
      <!--------END OF FILTERS--------------->

      <b-row>
        <b-col cols="12">
          <EmptyTable v-if="!tableItems.length" :values="emptyTable"/>
          <b-table v-else class="table-sm text-center" responsive bordered
                   :fields="tableFields" :items="tableItems">
            <template v-slot:options="row">
              <div class="flex-container options-center">
                <b-spinner v-if="spinners.isSaving === row.item.id"
                           type="grow" variant="primary" class="mr-3" small/>

                <span v-if="spinners.isSaving">
                  <a v-if="!row.item.is_active && spinners.isSaving !== row.item.id" class="mr-3">
                    <i class="fa fa-unlock-alt fa-lg fa-secondary-disabled"/>
                  </a>
                  <a v-if="row.item.is_active && spinners.isSaving !== row.item.id" class="mr-3">
                    <i class="fa fa-ban fa-lg fa-secondary-disabled"/>
                  </a>
                </span>
                <span v-else>
                  <a v-if="!row.item.is_active && spinners.isSaving !== row.item.id" class="mr-3"
                     @click="changeProductActive(row.item.id, true)">
                    <i class="fa fa-unlock-alt fa-lg fa-success"/>
                  </a>
                  <a v-if="row.item.is_active && spinners.isSaving !== row.item.id" class="mr-3"
                     @click="changeProductActive(row.item.id, false)">
                    <i class="fa fa-ban fa-lg fa-danger"/>
                  </a>
                </span>
                <Create class="mr-3" :createFromData="row.item.allData" :transportsClassifier="transportsClassifier"/>
                <Update :productId="row.item.id" :transportsClassifier="transportsClassifier"/>
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <!--Paginator-->
        <b-col cols="12" v-if="tableItems.length">
          <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange"/>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import Vue from 'vue';
import Pagination from 'laravel-vue-pagination';
import statuses from '@/store/statuses';
import { mapActions, mapGetters } from 'vuex';
import VuexTypes from '@/store/types/index';
import forEach from 'lodash.foreach';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import EmptyTable from '../../sharedComponents/EmptyTableData.vue';
import Create from './ProductCreate.vue';
import Update from './ProductUpdate.vue';

const ProcurementSchemeTypes = VuexTypes.procurementScheme;
export default {
  mounted() {},
  props: ['categoryId', 'page'],
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isSaving: false
      },
      filters: {
        name: null,
        has_category: false,
        is_active: null,
      },
      disabledButtons: {
        filter: false,
      },
      transportsClassifier: [],
      tableFields: [
        { key: 'id', label: this.$t('id', 'global') },
        { key: 'title', label: this.$t('title', 'global') },
        { key: 'category', label: this.$t('category', 'global') },
        { key: 'human_type', label: this.$t('type', 'global') },
        { key: 'is_ax_product', label: this.$t('AX_product', 'global') },
        { key: 'active', label: this.$t('active', 'global') },
        { key: 'options', label: this.$t('options', 'global') }
      ],
      tableItems: [],
      emptyTable: {
        text: this.$t('no_products_for_selected_category', 'global')
      },
      statusOptions: [
        { id: 0, label: this.$t('inactive', 'global') },
        { id: 1, label: this.$t('active', 'global') },
      ]
    };
  },
  components: { Treeselect, Pagination, EmptyTable, Create, Update },
  computed: {
    ...mapGetters('ProcurementScheme', [
      'productsList',
      'productCreate',
      'productUpdate',
      'productClassifiers',
      'productUpdateActive'
    ]),
    params() {
      const data = {};
      forEach(this.filters, (value, param) => {
        if (value || value === 0) {
          if (param === 'has_category') {
            data[param] = value === false ? 1 : 0;
          } else {
            data[param] = value;
          }
        }
      });
      data['page'] = this.page;
      data['category'] = this.categoryId;
      return data;
    },
    listPaginator() {
      return this.productsList.data.meta;
    },
    list() {
      return this.productsList;
    }
  },
  watch: {
    page: 'getData',
    categoryId: {
      immediate: true,
      handler() {
        this.getData(true);
      }
    },
    listPaginator: {
      handler: function () {
        if (this.listPaginator && this.listPaginator.last_page < this.page) {
          this.onPageChange(this.listPaginator.last_page);
        }
      },
      deep: true
    },
    list: {
      handler: function () {
        this.disabledButtons.filter = false;
        if (this.list['status'] === statuses.LOADED) {
          this.fillTableData();
          this.spinners.contentIsLoading = false;
        }
      },
      deep: true
    },
    productCreate: {
      handler: function () {
        if (this.productCreate.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.productCreate.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
    productUpdate: {
      handler: function () {
        if (this.productUpdate.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.productUpdate.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
    productClassifiers: {
      handler: function () {
        if (this.productClassifiers.status === statuses.LOADED) {
          const result = [];
          forEach(this.productClassifiers.data.transportsClassifier, (item) => {
            result.push(item);
          });
          this.transportsClassifier = result;
        }
      },
      deep: true
    },
    productUpdateActive: {
      handler: function () {
        this.spinners.isSaving = false;
        if (this.productUpdateActive.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.productUpdateActive.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('ProcurementScheme', [
      ProcurementSchemeTypes.PRODUCTS_LIST,
      ProcurementSchemeTypes.PRODUCT_UPDATE_ACTIVE,
      ProcurementSchemeTypes.PRODUCT_CLASSIFIERS,

    ]),
    getData(isCategoryChange) {
      this.disabledButtons.filter = true;
      if (isCategoryChange) {
        this.spinners.contentIsLoading = true;
      }
      this.PRODUCTS_LIST(this.params);
      this.PRODUCT_CLASSIFIERS();
    },
    cleanFilter() {
      if (this.$route.params.categoryId) {
        this.$router.push({ name: 'ProcurementScheme', params: { page: 1 } });
      }
      Vue.set(this.filters, 'name', null);
      Vue.set(this.filters, 'has_category', null);
      Vue.set(this.filters, 'is_active', null);
    },

    fillTableData() {
      const result = [];
      forEach(this.list.data.data, (product) => {
        result.push({
          id: product['id'],
          title: product['full_name'],
          category: product['category'] ? product['category']['name'] : '-',
          human_type: product['human_type'],
          is_ax_product: product['is_ax_product'] ? this.$t('yes', 'global') : this.$t('no', 'global'),
          active: product['is_active'] ? this.$t('yes', 'global') : this.$t('no', 'global'),
          is_active: product['is_active'],
          allData: product
        });
      });
      this.tableItems = result;
    },
    onPageChange(page) {
      if (!page) {
        page = this.page;
      }
      if (page !== this.page) {
        this.$router.push({ name: 'ProcurementScheme', params: { categoryId: this.categoryId, page } });
      }
    },
    changeProductActive(id, updateActive) {
      this.spinners.isSaving = id;
      const isActive = updateActive ? 1 : 0;
      this.PRODUCT_UPDATE_ACTIVE({ id, isActive });
    },
  }
};
</script>

<style scoped>
  .options-center {
    justify-content: center
  }
</style>
